// Ficha do utilizador: cabeçalho com estado e ações, e separadores (resumo, notas, compensações, histórico…).
import { useApi } from "@ecliptica/api-client/react";
import { isLang, useTranslation } from "@ecliptica/i18n";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  toast,
} from "@ecliptica/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Hourglass, ShieldCheck } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link, useParams } from "react-router";
import { useCan } from "../../features/auth/RequireStaff";
import { CompensationList, type CompensationItem } from "../../features/users/Compensations";
import { RoleBadge, StatusBadge } from "../../features/users/badges";
import { formatDate } from "../../lib/format";
import { SlaBadge, TicketStatusBadge } from "@ecliptica/game-ui";
import type { Status } from "@ecliptica/contexts-support";
import { UserActions } from "./UserActions";

function Row({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[9rem_1fr] gap-2 py-1.5 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}

export function UserDetailPage() {
  const { id = "" } = useParams();
  const { t, i18n } = useTranslation();
  const api = useApi();
  const can = useCan();
  const q = useQuery({
    queryKey: ["user", id],
    queryFn: async () => {
      const res = await api.v1.admin.users[":id"].$get({ param: { id } });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
  });

  if (q.isPending) return <Skeleton className="h-64 w-full" />;
  if (!q.data) return <EmptyState title={t("error.notFound")} />;
  const { user: u, accounts, sessions, notes, compensations, history, tickets } = q.data;
  const lang = i18n.language;

  return (
    <div className="flex flex-col gap-5">
      <Link to="/users" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" /> {t("nav.users")}
      </Link>
      <div className="flex flex-wrap items-center gap-4">
        <Avatar name={u.name} src={u.image ?? undefined} className="size-14 text-base" />
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-2xl font-extrabold text-gold">{u.name}</h1>
          <p className="truncate text-sm text-muted-foreground">{u.email}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <RoleBadge role={u.role} />
            <StatusBadge status={u.status} />
            {u.twoFactorEnabled && (
              <Badge variant="success">
                <ShieldCheck className="size-3" /> {t("users.twoFactor")}
              </Badge>
            )}
          </div>
        </div>
      </div>
      {u.banned && (
        <Alert variant={u.status === "banned" ? "destructive" : "warning"}>
          {t("users.overview.banInfo", { reason: u.banReason ?? "—" })} · {u.banExpires ? t("users.overview.banUntil", { date: formatDate(u.banExpires, lang) }) : t("users.overview.banForever")}
        </Alert>
      )}
      <UserActions user={u} />

      <Tabs defaultValue="overview">
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="overview">{t("users.tabs.overview")}</TabsTrigger>
          <TabsTrigger value="notes">
            {t("users.tabs.notes")} ({notes.length})
          </TabsTrigger>
          <TabsTrigger value="compensations">
            {t("users.tabs.compensations")} ({compensations.length})
          </TabsTrigger>
          {can("audit.read") && <TabsTrigger value="history">{t("users.tabs.history")}</TabsTrigger>}
          <TabsTrigger value="collection">{t("users.tabs.collection")}</TabsTrigger>
          <TabsTrigger value="tickets">
            {t("users.tabs.tickets")} ({tickets.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>{t("users.overview.profile")}</CardTitle>
            </CardHeader>
            <CardContent>
              <dl>
                <Row label={t("users.overview.id")}>
                  <code className="text-xs">{u.id}</code>
                </Row>
                <Row label={t("users.overview.email")}>{u.email}</Row>
                <Row label={t("users.overview.language")}>{isLang(u.locale) ? t(`languages.${u.locale}`) : (u.locale ?? "—")}</Row>
                <Row label={t("users.overview.created")}>{formatDate(u.createdAt, lang)}</Row>
                <Row label={t("users.overview.lastSeen")}>{u.lastSeen ? formatDate(u.lastSeen, lang) : t("users.never")}</Row>
                <Row label={t("users.overview.accounts")}>
                  <div className="flex flex-wrap gap-1.5">
                    {accounts.map((a) => (
                      <Badge key={a.providerId} variant="secondary" className="capitalize">
                        {a.providerId === "credential" ? t("users.overview.credential") : a.providerId}
                      </Badge>
                    ))}
                  </div>
                </Row>
              </dl>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>{t("users.overview.sessions")}</CardTitle>
            </CardHeader>
            <CardContent>
              {sessions.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("users.overview.noSessions")}</p>
              ) : (
                <ul className="flex flex-col gap-2 text-sm">
                  {sessions.map((s) => (
                    <li key={s.id} className="rounded-md border px-3 py-2">
                      <span className="block truncate">{s.userAgent?.replace(/\(.*?\)/g, "").slice(0, 70) ?? "—"}</span>
                      <span className="text-xs text-muted-foreground">
                        {s.ipAddress ?? "—"} · {formatDate(s.updatedAt, lang)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notes">
          <NotesTab userId={u.id} notes={notes} />
        </TabsContent>

        <TabsContent value="compensations" className="flex flex-col gap-3">
          <Alert>{t("users.comp.w7")}</Alert>
          {compensations.length ? <CompensationList items={compensations as CompensationItem[]} /> : <p className="text-sm text-muted-foreground">{t("users.comp.empty")}</p>}
        </TabsContent>

        <TabsContent value="history">
          <ul className="flex flex-col gap-2">
            {history.map((h) => (
              <li key={h.id} className="rounded-md border px-3 py-2 text-sm">
                <span className="font-semibold">{t(`audit.actions.${h.action.replace(".", "_")}`)}</span>
                <span className="text-muted-foreground">
                  {" "}
                  · {h.actorName ?? t("audit.system")} · {formatDate(h.at, lang)}
                </span>
                {h.reason && <p className="text-xs italic text-muted-foreground">{h.reason}</p>}
              </li>
            ))}
          </ul>
        </TabsContent>

        <TabsContent value="collection">
          <EmptyState icon={<Hourglass />} title={t("users.tabs.collection")}>
            {t("users.placeholder.collection")}
          </EmptyState>
        </TabsContent>
        <TabsContent value="tickets">
          {tickets.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("desk.empty")}</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {tickets.map((tk) => (
                <li key={tk.number}>
                  <Link to={`/support/${tk.number}`} className="flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-sm hover:border-gold/60">
                    <span className="font-mono text-xs text-muted-foreground">{tk.number}</span>
                    <span className="min-w-0 flex-1 truncate font-semibold">{tk.subject}</span>
                    <TicketStatusBadge status={tk.status as Status} />
                    <SlaBadge state={tk.sla.worst} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function NotesTab({ userId, notes }: { userId: string; notes: { id: number; body: string; createdAt: string; authorName: string | null }[] }) {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const qc = useQueryClient();
  const can = useCan();
  const [body, setBody] = useState("");
  async function save() {
    const res = await api.v1.admin.users[":id"].notes.$post({ param: { id: userId }, json: { body } });
    if (!res.ok) return toast.error(t("error.generic"));
    setBody("");
    await qc.invalidateQueries({ queryKey: ["user", userId] });
  }
  return (
    <div className="flex flex-col gap-3">
      {can("users.notes") && (
        <div className="flex flex-col gap-2">
          <Textarea placeholder={t("users.notes.placeholder")} maxLength={4000} value={body} onChange={(e) => setBody(e.target.value)} />
          <Button size="sm" className="self-start" disabled={!body.trim()} onClick={() => void save()}>
            {t("users.notes.add")}
          </Button>
        </div>
      )}
      {notes.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("users.notes.empty")}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {notes.map((n) => (
            <li key={n.id} className="rounded-md border px-3 py-2 text-sm">
              <p className="whitespace-pre-wrap">{n.body}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {n.authorName ?? "—"} · {formatDate(n.createdAt, i18n.language)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
